import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import Markdown from "./Markdown";
import { getRequiredConsents, acceptConsents } from "@/lib/api";

/**
 * Blocking consent step. Not dismissible: there is no close control, no
 * backdrop click and no escape key, because the point of it is that nobody sees
 * a deliverable without a recorded acceptance behind it.
 *
 * The accept control stays disabled until the reader has actually reached the
 * end of the operative document. That is the difference between a record that
 * says "they clicked" and one that says "they were shown the text and reached
 * the end of it before they clicked".
 *
 * The first document in the context is rendered in full. The rest are listed
 * with links, and accepting records every one of them - the server decides
 * which, not this component.
 */
export default function ConsentModal({ context, subjectRef = null, onAccepted, title, lede }) {
  const [state, setState] = useState({ loading: true, docs: [], error: null, busy: false });
  const [atEnd, setAtEnd] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await getRequiredConsents(context);
        if (!live) return;
        if (res.satisfied) { onAccepted?.(); return; }
        setState((s) => ({ ...s, loading: false, docs: res.documents || [] }));
      } catch (e) {
        if (!live) return;
        setState((s) => ({ ...s, loading: false, error: "Could not load the terms. Please reload." }));
      }
    })();
    return () => { live = false; };
  }, [context, onAccepted]);

  // A document short enough not to scroll must not trap the reader forever.
  const checkEnd = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const done = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (done) setAtEnd(true);
  }, []);

  useEffect(() => {
    if (!state.loading) requestAnimationFrame(checkEnd);
  }, [state.loading, checkEnd]);

  const accept = async () => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await acceptConsents(context, subjectRef);
      onAccepted?.();
    } catch (e) {
      setState((s) => ({
        ...s, busy: false,
        error: e?.response?.status === 401
          ? "Your session expired. Sign in again to continue."
          : "Could not record your acceptance. Please try again.",
      }));
    }
  };

  const primary = state.docs[0];
  const others = state.docs.slice(1);
  const anyDraft = state.docs.some((d) => d.is_draft);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#171717]/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
    >
      <div className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#f5f2ed] border border-[#c9c3b8] rounded-[10px] shadow-2xl overflow-hidden">
        <div className="px-6 md:px-8 pt-6 pb-4 border-b border-[#ddd8ce] shrink-0">
          <div className="tick text-[#9a3412] mb-2">Before you continue</div>
          <h2 id="consent-title" className="font-serif-display text-2xl md:text-3xl text-[#171717] leading-snug">
            {title || primary?.title || "Please read and accept"}
          </h2>
          {lede && <p className="mt-2 font-mono-tech text-[12px] text-[#525252] leading-relaxed">{lede}</p>}
        </div>

        {anyDraft && (
          <div className="mx-6 md:mx-8 mt-4 flex gap-2 items-start px-3 py-2 border border-[#9a3412]/40 bg-[#9a3412]/[0.06] rounded shrink-0">
            <AlertTriangle size={13} className="text-[#9a3412] mt-0.5 shrink-0" />
            <p className="font-mono-tech text-[11px] text-[#9a3412] leading-relaxed">
              Draft. These terms have not yet been reviewed by an Australian legal
              practitioner and are not in force.
            </p>
          </div>
        )}

        <div
          ref={scroller}
          onScroll={checkEnd}
          className="flex-1 overflow-y-auto px-6 md:px-8 py-6 font-mono-tech text-[13px]"
          data-testid="consent-scroll"
        >
          {state.loading ? (
            <div className="flex items-center gap-2 text-[#525252]">
              <Loader2 size={14} className="animate-spin" /> Loading the terms…
            </div>
          ) : (
            <>
              {primary && <Markdown source={primary.body} />}
              {others.length > 0 && (
                <div className="mt-8 pt-6 border-t border-[#c9c3b8]">
                  <div className="tick mb-3 text-[#9a3412]">You are also accepting</div>
                  <ul className="space-y-2">
                    {others.map((d) => (
                      <li key={d.slug}>
                        <a
                          href={`/legal/${d.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#9a3412] underline underline-offset-2 hover:text-[#171717]"
                        >
                          {d.title}
                        </a>
                        <span className="text-[#8a857d]"> · v{d.version}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 md:px-8 py-4 border-t border-[#ddd8ce] bg-[#efebe4] shrink-0">
          {state.error && (
            <p className="mb-3 font-mono-tech text-[12px] text-[#9a3412]">{state.error}</p>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="font-mono-tech text-[11px] text-[#525252] leading-relaxed">
              {atEnd
                ? "We record the version you accept, the date, and your IP address."
                : "Scroll to the end to continue."}
            </p>
            <button
              onClick={accept}
              disabled={!atEnd || state.busy || state.loading}
              data-testid="consent-accept"
              className="btn-primary shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {state.busy
                ? <><Loader2 size={13} className="animate-spin" /> Recording…</>
                : <><Check size={13} /> I have read and accept</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
